export type IntercomEvent = 'intercom_boot' | 'intercom_identify' | 'intercom_update' | 'intercom_shutdown';

export type IntercomBootPayload = {
  sitename?: string;
  dashboard_url?: string;
};

export type IntercomIdentifyPayload = {
  email: string;
  name: string;
  astur_user_id: string;
  organization_id: string;
  organization_name: string;
  sitename: string;
  dashboard_url: string;
};

export type IntercomUpdatePayload = {
  sitename: string;
  dashboard_url: string;
  organization_id: string;
  organization_name: string;
};

export type IntercomShutdownPayload = Record<string, never>;

export type IntercomPayload = IntercomBootPayload | IntercomIdentifyPayload | IntercomUpdatePayload | IntercomShutdownPayload;

export const getDashboardUrl = (sitename: string): string => {
  if (typeof window === 'undefined') return '';
  const origin = window.location.origin;
  return sitename ? `${origin}/${sitename}/` : `${origin}/`;
};

// PII: payloads include email + name. The matching GTM triggers must fire
// the Intercom tag only — never a generic page-view tag (GA4, Meta, etc.) —
// or this leaves the browser on every Intercom event.
//
// onComplete maps to GTM's `eventCallback` / `eventTimeout` protocol: GTM
// runs the callback after every tag triggered by this event finishes (or
// after eventTimeout ms, whichever first). Use it to defer navigation —
// e.g. logout — until the tag has actually fired.
//
// Safety net: eventCallback/eventTimeout are enforced by gtm.js after it
// patches dataLayer.push. If GTM is blocked (ad blockers, corporate
// firewalls, network failure) push degrades to Array.prototype.push and
// the callback is never invoked — so we also fire onComplete via a JS
// timeout. The idempotency guard prevents double-invocation when GTM is
// present.
export function pushIntercomEvent(event: 'intercom_boot', payload?: IntercomBootPayload, onComplete?: () => void): void;
export function pushIntercomEvent(event: 'intercom_identify', payload: IntercomIdentifyPayload, onComplete?: () => void): void;
export function pushIntercomEvent(event: 'intercom_update', payload: IntercomUpdatePayload, onComplete?: () => void): void;
export function pushIntercomEvent(event: 'intercom_shutdown', payload?: IntercomShutdownPayload, onComplete?: () => void): void;
export function pushIntercomEvent(event: IntercomEvent, payload: IntercomPayload = {}, onComplete?: () => void): void {
  if (typeof window === 'undefined') {
    onComplete?.();
    return;
  }
  window.dataLayer = window.dataLayer || [];

  let completed = false;
  const safeComplete = onComplete
    ? () => {
        if (completed) return;
        completed = true;
        onComplete();
      }
    : undefined;

  window.dataLayer.push({
    event,
    ...payload,
    ...(safeComplete ? { eventCallback: safeComplete, eventTimeout: 2000 } : {}),
  });

  if (safeComplete) {
    setTimeout(safeComplete, 2500);
  }
}
